import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ChatState, ChatMessage, MessageRole, MessageStatus } from '@/types'
import { createInitialChatState, generateMessageId, generateSessionId } from '@/types/chat.types'

const initialState: ChatState = createInitialChatState()

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // ========================================
    // PANEL STATE
    // ========================================
    openChat: (state) => {
      state.isOpen = true
      state.isMinimized = false
      if (!state.sessionId) {
        state.sessionId = generateSessionId()
        state.startedAt = new Date().toISOString()
      }
    },
    
    closeChat: (state) => {
      state.isOpen = false
      state.isMinimized = false
    },
    
    toggleChat: (state) => {
      state.isOpen = !state.isOpen
      if (state.isOpen) {
        state.isMinimized = false
        if (!state.sessionId) {
          state.sessionId = generateSessionId()
          state.startedAt = new Date().toISOString()
        }
      }
    },
    
    minimizeChat: (state) => {
      state.isMinimized = true
    },
    
    maximizeChat: (state) => {
      state.isMinimized = false
    },
    
    // ========================================
    // MESSAGES
    // ========================================
    addMessage: (state, action: PayloadAction<{
      role: MessageRole
      content: string
      status?: MessageStatus
    }>) => {
      const message: ChatMessage = {
        id: generateMessageId(),
        role: action.payload.role,
        content: action.payload.content,
        timestamp: new Date().toISOString(),
        status: action.payload.status || 'sent',
      }
      state.messages.push(message)
    },
    
    addUserMessage: (state, action: PayloadAction<string>) => {
      const message: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: action.payload,
        timestamp: new Date().toISOString(),
        status: 'sent',
      }
      state.messages.push(message)
      state.suggestedPromptsVisible = false
    },
    
    addAssistantMessage: (state, action: PayloadAction<{
      content: string
      isStreaming?: boolean
      tokenCount?: number
      responseTime?: number
    }>) => {
      const message: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: action.payload.content,
        timestamp: new Date().toISOString(),
        status: 'sent',
        isStreaming: action.payload.isStreaming,
        tokenCount: action.payload.tokenCount,
        responseTime: action.payload.responseTime,
      }
      state.messages.push(message)
    },
    
    updateLastMessage: (state, action: PayloadAction<Partial<ChatMessage>>) => {
      const lastMessage = state.messages[state.messages.length - 1]
      if (lastMessage) {
        Object.assign(lastMessage, action.payload)
      }
    },
    
    appendToLastMessage: (state, action: PayloadAction<string>) => {
      const lastMessage = state.messages[state.messages.length - 1]
      if (lastMessage && lastMessage.role === 'assistant') {
        lastMessage.content += action.payload
      }
    },
    
    setMessageStatus: (state, action: PayloadAction<{ id: string; status: MessageStatus; error?: string }>) => {
      const message = state.messages.find(m => m.id === action.payload.id)
      if (message) {
        message.status = action.payload.status
        if (action.payload.error) {
          message.error = action.payload.error
        }
      }
    },
    
    removeMessage: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter(m => m.id !== action.payload)
    },
    
    clearMessages: (state) => {
      state.messages = []
      state.suggestedPromptsVisible = true
    },
    
    // ========================================
    // LOADING & ERROR
    // ========================================
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.isLoading = false
    },
    
    clearError: (state) => {
      state.error = null
    },
    
    // ========================================
    // SUGGESTED PROMPTS
    // ========================================
    showSuggestedPrompts: (state) => {
      state.suggestedPromptsVisible = true
    },
    
    hideSuggestedPrompts: (state) => {
      state.suggestedPromptsVisible = false
    },
    
    // ========================================
    // SESSION
    // ========================================
    startNewSession: (state) => {
      state.messages = []
      state.sessionId = generateSessionId()
      state.startedAt = new Date().toISOString()
      state.error = null
      state.isLoading = false
      state.suggestedPromptsVisible = true
    },
    
    // ========================================
    // FULL STATE
    // ========================================
    setChatData: (state, action: PayloadAction<Partial<ChatState>>) => {
      Object.assign(state, action.payload)
    },
    
    resetChat: () => createInitialChatState(),
  },
})

export const {
  openChat,
  closeChat,
  toggleChat,
  minimizeChat,
  maximizeChat,
  addMessage,
  addUserMessage,
  addAssistantMessage,
  updateLastMessage,
  appendToLastMessage,
  setMessageStatus,
  removeMessage,
  clearMessages,
  setLoading,
  setError,
  clearError,
  showSuggestedPrompts,
  hideSuggestedPrompts,
  startNewSession,
  setChatData,
  resetChat,
} = chatSlice.actions

export default chatSlice.reducer
